import { Alert, Button, Card, Table, Timeline } from "flowbite-react";
import React, { useEffect, useRef, useState } from "react";
import {
  formatDateToDDMM,
  formatDateToYY,
  formatDocument,
  formatToReal,
  getCompanyAge,
  getLetterScore,
  getScoreLegends,
  isValidCNPJ,
  isValidCPF,
  maskCEP,
  parseFoundationDate,
} from "../../utils/utils";
import axios from "../../config/axiosInstance";
import { QUERIES } from "../../config/apiConfig";
import { HiInformationCircle, HiThumbDown, HiThumbUp } from "react-icons/hi";
import HeaderQuery from "./HeaderQuery";
import usePDF from "../../hooks/usePDF";
import TitleStripe from "../common/TitleStripe";
import CardQueryDescription from "../common/CardQueryDescription";
import QueryInput from "../inputs/QueryInput";
import SliderToPreview from "../common/SliderToPreview";
import QueryLoading from "../common/QueryLoading";
import AvisoCard from "../common/query/AvisoCard";
import PendenciasFinanceiras from "../common/query/PendenciasFinanceiras";
import NoOcurrence from "../common/query/NoOcurrence";
import Protestos from "../common/query/Protestos";
import ChequesSemFundos from "../common/query/ChequesSemFundos";
import QuadroSocietario from "../common/query/QuadroSocietario";
import FooterQuery from "./FooterQuery";
import moment from "moment";
import { EvaluationScorePJ } from "../common/query/EvaluationScorePJ";
import HistoricoConsultas from "../common/query/HistoricoConsultas";
import { FaPrint } from "react-icons/fa";
import { useReactToPrint } from "react-to-print";
import ChequesSustados from "../common/query/ChequesSustados";
import AcoesCiveis from "../common/query/AcoesCiveis";
import FalenciaRecuperacaoJudicial from "../common/query/FalenciaRecuperacaoJudicial";
import PositivoPlusCNPJQuery from "./PositivoPlusCNPJQuery";
import { LuBuilding2, LuMapPin } from "react-icons/lu";
import { GrMoney } from "react-icons/gr";
import { BsBank } from "react-icons/bs";
import { HiExclamationCircle, HiShieldCheck } from "react-icons/hi2";
import { TfiBarChart } from "react-icons/tfi";

const CompletaPlusCNPJQuery = ({
  query,
  selectedPrice,
  dataFromLocalStorage = null,
  hasHeaderPDF = "true",
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [info, setInfo] = useState("");
  const [error, setError] = useState("");
  const [errorData, setErrorData] = useState("");
  const [resumoRetorno, setResumoRetorno] = useState(null);
  const [data, setData] = useState(null);
  const [document, setDocument] = useState("");
  const componentRef = useRef();
  const reactToPrintFn = useReactToPrint({ contentRef: componentRef });

  const probRaw = +data?.score?.score_pj?.probabilidade;
  const prob = typeof probRaw === "number" ? probRaw : null;

  useEffect(() => {
    if (!dataFromLocalStorage) return;

    setData(dataFromLocalStorage);
    setDocument(
      dataFromLocalStorage?.consultaCredito?.dadosCadastrais?.cnpj ||
        dataFromLocalStorage?.defineLimitePositivo?.identificacao_completo
          ?.cnpj ||
        dataFromLocalStorage?.resumoRetorno?.document ||
        ""
    );
  }, [dataFromLocalStorage]);

  const fetchInfo = async (type) => {
    return await axios(
      `${QUERIES.GET_V2_QUERY}/completa-plus-cnpj/${info.replace(/\D/g, "")}`
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    setDocument(info);
    const unmaskedValue = info.replace(/\D/g, "");
    let isValid = true;
    let type = "F";

    if (unmaskedValue.length <= 11) {
      if (!isValidCPF(unmaskedValue)) {
        setError("CPF inválido!");
        isValid = false;
      }
    } else {
      if (!isValidCNPJ(unmaskedValue)) {
        setError("CNPJ inválido!");
        isValid = false;
      }
      type = "J";
    }

    if (isValid) {
      try {
        setData(null);
        setIsLoading(true);
        setErrorData("");
        const { data: dados } = await fetchInfo(type);
        setData({
          ...dados.data,
          ...dados?.data?.defineLimitePositivo,
        });
        setResumoRetorno(dados?.data?.resumoRetorno);
      } catch (error) {
        setErrorData(
          typeof error?.response?.data?.message !== "object"
            ? error?.response?.data?.message
            : "Algum erro ocorreu com sua consulta"
        );
      } finally {
        setIsLoading(false);
      }
    } else {
      setData(null);
    }
  };

  // Desde out/2026 a Relinq devolve este produto com os mesmos blocos da
  // Essencial Plus CNPJ (Define Risco, SPC, Receita e rating) no lugar do
  // Define Limite Positivo e do Crednet; aí o laudo é o da Essencial.
  const formatoDefineRisco =
    !!data?.defineRisco && !data?.defineLimitePositivo;

  const fundacaoMoment = parseFoundationDate(
    data?.identificacao_completo?.data_fundacao
  );
  const companyAge = getCompanyAge(fundacaoMoment);
  const companyAgeLabel =
    fundacaoMoment && companyAge
      ? `${companyAge.years} ano${companyAge.years !== 1 ? "s" : ""}${
          companyAge.months
            ? ` e ${companyAge.months} mes${
                companyAge.months !== 1 ? "es" : ""
              }`
            : ""
        }`
      : null;
  const fundacaoLabel = fundacaoMoment
    ? fundacaoMoment.format("DD/MM/YYYY")
    : null;

  let riskLabel = "Indefinido";
  let riskClasses = {
    chip: "bg-gray-100 text-gray-700",
    ring: "from-slate-50 to-slate-100",
    dotActive: "bg-gray-300",
    dotIdle: "bg-gray-100",
  };

  if (prob !== null) {
    if (prob < 10) {
      riskLabel = "Baixo";
      riskClasses = {
        chip: "bg-green-100 text-green-700",
        ring: "from-green-50 to-teal-50",
        dotActive: "bg-green-500",
        dotIdle: "bg-green-100",
      };
    } else if (prob < 25) {
      riskLabel = "Moderado";
      riskClasses = {
        chip: "bg-amber-100 text-amber-700",
        ring: "from-amber-50 to-yellow-50",
        dotActive: "bg-amber-500",
        dotIdle: "bg-amber-100",
      };
    } else if (prob < 60) {
      riskLabel = "Alto";
      riskClasses = {
        chip: "bg-orange-100 text-orange-700",
        ring: "from-orange-50 to-amber-50",
        dotActive: "bg-orange-500",
        dotIdle: "bg-orange-100",
      };
    } else {
      riskLabel = "Altíssimo";
      riskClasses = {
        chip: "bg-rose-100 text-rose-700",
        ring: "from-rose-50 to-red-50",
        dotActive: "bg-rose-500",
        dotIdle: "bg-rose-100",
      };
    }
  }

  const statusConsumidor = data?.status_consumidor?.mensagem;
  const statusConsumidorContainsNao = statusConsumidor
    ? statusConsumidor.toUpperCase().includes("NÃO") ||
      statusConsumidor.toUpperCase().includes("NAO") ||
      statusConsumidor.toUpperCase().includes("CANCELADA") ||
      statusConsumidor.toUpperCase().includes("SEM")
    : false;
  const eventosImportantesRaw =
    data?.demais_informacoes?.acontecimentos_importantes
      ?.historico_periodo_pontuacao ?? [];
  const eventosImportantes = eventosImportantesRaw.filter((evento) => {
    if (!evento) {
      return false;
    }
    const periodo = evento.periodo?.toString()?.toLowerCase() ?? "";
    const indicador = evento.indicador?.toString()?.toLowerCase() ?? "";
    if (periodo === "data" && indicador.includes("acontecimentos")) {
      return false;
    }
    return Boolean(evento.periodo || evento.indicador || evento.pontuacao);
  });
  const tempoRelacionamento =
    data?.demais_informacoes?.tempo_relacionamento_fornecedores
      ?.historico_periodo ?? [];
  const tempoValores = tempoRelacionamento.map((item) => {
    const valor = Number(item?.valor ?? 0);
    return Number.isFinite(valor) ? valor : 0;
  });
  const maxTempoValor = tempoValores.length > 0 ? Math.max(...tempoValores) : 0;
  const hasDemaisInformacoes =
    eventosImportantes.length > 0 || tempoRelacionamento.length > 0;
  const getTempoBarWidth = (valor) => {
    const safeValor = Number.isFinite(valor) ? valor : 0;
    if (maxTempoValor <= 0) {
      return 0;
    }
    const percent = (safeValor / maxTempoValor) * 100;
    return Math.min(Math.max(percent, 4), 100);
  };
  const compromissos =
    data?.comprometimento_futuro?.lista_compromissos?.compromisso ?? [];
  const creditos = data?.comprometimento_futuro?.lista_credito?.credito ?? [];
  const periodosCompromissos = compromissos
    .map((item) => item.periodo)
    .filter(Boolean);
  const periodosCreditos = creditos.map((item) => item.periodo).filter(Boolean);
  const periodos = [...new Set([...periodosCompromissos, ...periodosCreditos])];
  const getCompromissoPorPeriodo = (periodo) =>
    compromissos.find((item) => item.periodo === periodo);
  const getCreditoPorPeriodo = (periodo) =>
    creditos.find((item) => item.periodo === periodo);
  const formatQTD = (valor) => {
    const numero = parseFloat(valor);
    if (isNaN(numero)) {
      return "–";
    }
    if (numero === 0) {
      return "< 10";
    }
    return numero * 100;
  };
  const formatValorTitulos = (valor) => {
    const numero = parseFloat(valor);
    return !isNaN(numero) ? formatToReal(numero * 1000) : "–";
  };
  const normalizeArray = (value) =>
    Array.isArray(value) ? value : value ? [value] : [];
  const hasQtd = (qtd) => qtd != null && String(qtd) !== "0";
  const renderValor = (valor) =>
    valor != null && String(valor) !== "0" ? formatToReal(valor) : "—";
  const renderPendenciasParticipacao = (socio) => {
    const indicador = String(socio?.indicador_debito || "").toUpperCase();
    if (indicador !== "SIM") {
      return <div className="text-emerald-600">Não há débitos</div>;
    }

    const restr = socio?.restricoes_documento || {};
    const itens = [];

    if (hasQtd(restr?.cheques_sem_fundo?.quantidade)) {
      itens.push(
        <div key="cheques">
          Cheques sem fundos:{" "}
          <span className="text-rose-600 font-semibold">
            {restr?.cheques_sem_fundo?.quantidade} ocorrências
          </span>
        </div>
      );
    }

    if (hasQtd(restr?.protestos?.quantidade)) {
      itens.push(
        <div key="protestos">
          Protestos:{" "}
          <span className="text-rose-600 font-semibold">
            {renderValor(restr?.protestos?.valor)}
          </span>
        </div>
      );
    }

    if (hasQtd(restr?.pendencias_restricoes?.quantidade)) {
      itens.push(
        <div key="pendencias">
          Pendências:{" "}
          <span className="text-rose-600 font-semibold">
            {renderValor(restr?.pendencias_restricoes?.valor)}
          </span>
        </div>
      );
    }

    if (hasQtd(restr?.acoes_judiciais?.quantidade)) {
      itens.push(
        <div key="acoes">
          Ações cíveis:{" "}
          <span className="text-rose-600 font-semibold">
            {renderValor(restr?.acoes_judiciais?.valor)}
          </span>
        </div>
      );
    }

    if (hasQtd(restr?.falencias_recuperacao_judicial?.quantidade)) {
      itens.push(
        <div key="falencias">
          Rec. judicial/Falências:{" "}
          <span className="text-rose-600 font-semibold">
            {renderValor(restr?.falencias_recuperacao_judicial?.valor)}
          </span>
        </div>
      );
    }

    if (!itens.length) {
      return <div className="text-emerald-600">Não há débitos</div>;
    }

    return <div className="space-y-1">{itens}</div>;
  };
  const renderParticipacoesTable = (sociosParticipantes) => {
    const rows = normalizeArray(sociosParticipantes);
    if (!rows.length) return <NoOcurrence />;

    return (
      <div className="mt-3 mb-5 overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <div className="border-b border-slate-200 bg-slate-50 px-4 py-3">
          <div className="grid grid-cols-7 gap-3 text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-500">
            <div className="col-span-2">Denominação social</div>
            <div className="text-center">Entrada</div>
            <div className="text-center">Part. (%)</div>
            <div className="text-center">Situação</div>
            <div className="col-span-2">Pendências</div>
          </div>
        </div>

        <div className="divide-y divide-slate-100">
          {rows.map((socio, index) => (
            <div
              key={index}
              className={`grid grid-cols-7 gap-3 px-4 py-3 text-sm items-start ${
                index % 2 === 0 ? "bg-white" : "bg-slate-50/40"
              } hover:bg-slate-50`}
            >
              <div className="col-span-2">
                <div className="font-medium text-slate-900">
                  {socio?.razao_social || "-"}
                </div>
                <div className="text-[11px] text-slate-500">
                  CNPJ: {socio?.cnpj ? formatDocument(socio?.cnpj) : "-"}
                </div>
              </div>
              <div className="text-center text-slate-700">
                {socio?.entrada || "-"}
              </div>
              <div className="text-center text-slate-700">
                {socio?.participacao || "-"}
              </div>
              <div className="text-center">
                <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-600">
                  {socio?.situacao_cnpj || "-"}
                </span>
              </div>
              <div className="col-span-2 text-[11px] text-slate-600">
                {renderPendenciasParticipacao(socio)}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };
  const sociosAdmOutrasEmpresas = normalizeArray(
    data?.participacoes_socios_adm_outras_empresas?.socio
  );
  const compromissosIndicadores =
    data?.compromissos?.lista_indicador_comportamento?.item_indicador ?? [];
  const creditosIndicadores =
    data?.credito_obtido?.lista_indicador_comportamento?.item_indicador ?? [];
  const periodosCompromissosIndicadores = compromissosIndicadores
    .map((item) => item.mes_ano)
    .filter(Boolean);
  const periodosCreditosIndicadores = creditosIndicadores
    .map((item) => item.mes_ano)
    .filter(Boolean);
  const periodosIndicadores = [
    ...periodosCompromissosIndicadores,
    ...periodosCreditosIndicadores,
  ].filter((periodo, index, self) => self.indexOf(periodo) === index);
  const getCompromissoIndicadorPorPeriodo = (periodo) =>
    compromissosIndicadores.find((item) => item.mes_ano === periodo);
  const getCreditoIndicadorPorPeriodo = (periodo) =>
    creditosIndicadores.find((item) => item.mes_ano === periodo);

  return (
    <>
      {!dataFromLocalStorage && (
        <>
          <CardQueryDescription
            title={query?.name || "Informações da consulta"}
            infos={[
              "Pendências Financeiras",
              "Protestos",
              "Cheques sem Fundos",
              "Cadin",
            ]}
            price={selectedPrice}
          >
            <form
              className="flex flex-col gap-2 bg-white p-2 rounded-lg shadow-md w-fit"
              onSubmit={handleSubmit}
            >
              <div className="flex flex-col md:flex-row gap-2 items-center">
                <QueryInput
                  info={info}
                  setInfo={setInfo}
                  isLoading={isLoading}
                  error={error}
                  type="CNPJ"
                />
                {!errorData && data && (
                  <Button
                    className="bg-gray-600"
                    onClick={() => reactToPrintFn()}
                  >
                    <div className="flex gap-2 items-center">
                      <FaPrint />
                      <div>Imprimir</div>
                    </div>
                  </Button>
                )}
              </div>
            </form>
          </CardQueryDescription>
        </>
      )}

      {!formatoDefineRisco && <SliderToPreview />}

      {errorData && (
        <Alert color="failure" icon={HiInformationCircle}>
          <span className="font-medium">{errorData}</span>
        </Alert>
      )}
      {!errorData && formatoDefineRisco && (
        <PositivoPlusCNPJQuery
          query={query}
          dataFromLocalStorage={data}
          hasHeaderPDF={hasHeaderPDF}
          printRef={componentRef}
        />
      )}
      {!errorData && !formatoDefineRisco && (
        <div
          className={`overflow-x-scroll print-content mobile-fit ${
            data || isLoading ? "block" : "hidden"
          }`}
          ref={componentRef}
        >
          {!isLoading ? (
            <Card className="p-5 card-pdf relative overflow-hidden">
              <HeaderQuery
                title={query?.name}
                hasHeader={hasHeaderPDF}
                date={data?.resumoRetorno?.dataConsulta}
              />

              <TitleStripe>Quadro de avisos</TitleStripe>
              <div className="container-alerts three-columns">
                <>
                  <AvisoCard
                    title="Pendências Serasa"
                    hasRestrictions={
                      +data?.crednet?.consultaCredito?.resumoConsulta
                        ?.pendenciasFinanceiras?.quantidadeTotal > 0
                    }
                    messageError={`${data?.crednet?.consultaCredito?.resumoConsulta?.pendenciasFinanceiras?.quantidadeTotal} ocorrências`}
                  />

                  <AvisoCard
                    title="Pendências QUOD"
                    hasRestrictions={
                      +data?.quod?.resumoConsulta?.pendenciasFinanceiras
                        ?.quantidadeTotal > 0
                    }
                    messageError={`${data?.quod?.resumoConsulta?.pendenciasFinanceiras?.quantidadeTotal} ocorrências`}
                  />

                  <AvisoCard
                    title="Cheques sem Fundos"
                    hasRestrictions={!data?.cheques_sem_fundo?.mensagem}
                    messageError={`${data?.cheques_sem_fundo?.total_cheques_sem_fundos} 
                                                ocorrências`}
                  />
                  <AvisoCard
                    title="Cheques Sustados"
                    hasRestrictions={!data?.cheques_sustados?.mensagem}
                    messageError={`${data?.cheques_sustados?.total} ocorrências`}
                  />
                  <AvisoCard
                    title="Protestos"
                    hasRestrictions={!data?.protestos?.mensagem}
                    messageError={`${data?.protestos?.total_protestos} ocorrências`}
                  />
                  <AvisoCard
                    title="Negativações"
                    hasRestrictions={!data?.pendencias_restricoes?.mensagem}
                    messageError={`${data?.pendencias_restricoes?.total_pendencias}
                                            ocorrências`}
                  />
                  <AvisoCard
                    title="Ações Cíveis"
                    hasRestrictions={!data?.acoes_civeis?.mensagem}
                    messageError={`${data?.acoes_civeis?.total_acoes} ocorrências`}
                  />
                  <AvisoCard
                    title="Falência/Recuperação"
                    hasRestrictions={
                      !data?.falencias_recuperacao_judicial?.mensagem
                    }
                    messageError={`${data?.falencias_recuperacao_judicial?.quantidade_total} ocorrências`}
                  />
                </>
              </div>

              <TitleStripe>
                <div className="flex items-center gap-2">
                  <LuBuilding2 className="text-blue-700" />
                  <span>Dados Cadastrais</span>
                </div>
              </TitleStripe>

              <div className="mt-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
                      Razão Social
                    </p>
                    <p className="text-xl font-semibold text-gray-900">
                      {data?.identificacao_completo?.razao_social ||
                        "Indisponível"}
                    </p>
                    <p className="text-[11px] text-gray-500">
                      {data?.identificacao_completo?.nome_fantasia ||
                        "Sem nome fantasia"}
                    </p>
                  </div>
                </div>

                <div className="mt-2 flex flex-wrap gap-2 text-[10px] text-gray-500">
                  <span className="inline-flex items-center rounded-full border border-gray-200 bg-slate-50 px-2 py-0.5">
                    {data?.identificacao_completo?.situacao_cnpj ||
                      "Situação desconhecida"}
                  </span>
                  <span className="inline-flex items-center rounded-full border border-gray-200 bg-slate-50 px-2 py-0.5">
                    Fundação: {fundacaoLabel || "—"}
                  </span>
                  {companyAgeLabel && (
                    <span className="inline-flex items-center rounded-full border border-gray-200 bg-slate-50 px-2 py-0.5">
                      {companyAgeLabel}
                    </span>
                  )}
                </div>

                <div className="mt-3 grid gap-2 grid-cols-3">
                  {[
                    {
                      label: "CNPJ",
                      value: data?.identificacao_completo?.cnpj
                        ? formatDocument(data?.identificacao_completo?.cnpj)
                        : "Não disponível",
                      hint: "Documento",
                    },
                    {
                      label: "Inscrição Estadual",
                      value:
                        data?.identificacao_completo?.inscricao_estadual ||
                        "Não informado",
                      hint: "Inscrição",
                    },
                    {
                      label: "CNAE principal",
                      value:
                        data?.identificacao_completo?.ramo_atividade_primario
                          ?.atividade || "Não informado",
                      hint: "Atividade",
                    },
                    {
                      label: "Segmento",
                      value:
                        data?.identificacao_completo?.segmento ||
                        "Não informado",
                      hint: "Setor",
                    },
                    {
                      label: "Natureza jurídica",
                      value:
                        data?.identificacao_completo?.natureza_juridica ||
                        "Não informado",
                      hint: "Perfil",
                    },
                    {
                      label: "Capital social",
                      value:
                        formatToReal(
                          data?.identificacao_completo?.capital_inicial
                        ) || "—",
                      hint: "Recursos iniciais",
                    },
                  ].map((item) => (
                    <div
                      key={item.label}
                      className="rounded-xl border border-gray-100 bg-slate-50 px-3 py-2"
                    >
                      <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
                        {item.label}
                      </p>
                      <p className="text-sm font-semibold text-gray-900">
                        {item.value}
                      </p>
                      <p className="text-[10px] text-gray-500">{item.hint}</p>
                    </div>
                  ))}
                </div>
              </div>

              <TitleStripe className="mt-6 mb-3">
                <div className="flex items-center gap-2">
                  <LuMapPin className="text-blue-700" />{" "}
                  <span>Localização</span>
                </div>
              </TitleStripe>

              <div>
                <div className="grid gap-2 sm:grid-cols-2">
                  <div className="rounded-xl border border-gray-200 bg-white px-3 py-2">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
                      Endereço
                    </p>
                    <div className="flex items-center gap-2">
                      <p className="text-[12px] font-semibold text-gray-900">
                        {data?.localizacao_completo?.matriz?.endereco_matriz ||
                          "—"}
                      </p>
                    </div>
                  </div>
                  <div className="rounded-xl border border-gray-200 bg-white px-3 py-2">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
                      Bairro · CEP
                    </p>
                    <p className="text-[12px] font-semibold text-gray-900 flex flex-wrap gap-1">
                      <span>
                        {data?.localizacao_completo?.matriz?.bairro_matriz ||
                          "—"}
                      </span>
                      <span>·</span>
                      <span>
                        {maskCEP(
                          data?.localizacao_completo?.matriz?.cep_matriz
                        ) || "--"}
                      </span>
                    </p>
                  </div>
                </div>

                <div className="mt-2 grid gap-2 sm:grid-cols-2">
                  <div className="rounded-xl border border-gray-200 bg-white px-3 py-2">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
                      Cidade
                    </p>
                    <p className="text-[12px] font-semibold text-gray-900">
                      {data?.localizacao_completo?.matriz?.cidade_matriz || "—"}
                    </p>
                  </div>
                  <div className="rounded-xl border border-gray-200 bg-white px-3 py-2">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
                      UF
                    </p>
                    <p className="text-[12px] font-semibold text-gray-900">
                      {data?.localizacao_completo?.matriz?.uf_matriz || "--"}
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-6 bg-white text-sm space-y-6">
                {/* Limite de Crédito */}
                <div className="grid gap-4 -mt-10 grid-cols-2">
                  <div>
                    <TitleStripe>
                      <div className="flex items-center gap-2">
                        <GrMoney className="text-blue-700" />{" "}
                        <span>Faturamento Presumido</span>
                      </div>
                    </TitleStripe>
                    <div className="mt-3 rounded-2xl border border-slate-200 bg-white shadow-sm">
                      <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-amber-200 bg-amber-50 text-amber-700">
                            <GrMoney className="h-4 w-4" />
                          </span>
                          <div>
                            <div className="text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-500">
                              Estimativa anual
                            </div>
                            <div className="text-xs text-slate-400">
                              Base cadastral e comportamental
                            </div>
                          </div>
                        </div>
                        <span className="hidden rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-amber-700 sm:inline-flex">
                          Presumido
                        </span>
                      </div>

                      <div className="px-4 py-4">
                        <div className="text-xl font-extrabold text-slate-900">
                          {typeof data?.faturamento_presumido
                            ?.faturamento_anual === "number"
                            ? formatToReal(
                                data.faturamento_presumido.faturamento_anual
                              )
                            : data?.faturamento_presumido?.faturamento_anual ||
                              "Indisponível"}
                        </div>

                        {data?.faturamento_presumido?.mensagem && (
                          <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-3 text-[11px] text-slate-600">
                            {data.faturamento_presumido.mensagem}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div>
                    <TitleStripe>
                      <div className="flex items-center gap-2">
                        <BsBank className="text-blue-700" />{" "}
                        <span>Limite de crédito</span>
                      </div>
                    </TitleStripe>
                    <div className="mt-3 rounded-2xl border border-slate-200 bg-white shadow-sm">
                      <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-blue-200 bg-blue-50 text-blue-700">
                            <BsBank className="h-4 w-4" />
                          </span>
                          <div>
                            <div className="text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-500">
                              Estimativa sugerida
                            </div>
                            <div className="text-xs text-slate-400">
                              Limite recomendado
                            </div>
                          </div>
                        </div>
                        <span className="hidden rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-blue-700 sm:inline-flex">
                          Crédito
                        </span>
                      </div>

                      <div className="px-4 py-4">
                        <div className="text-xl font-extrabold text-slate-900">
                          {data?.limite_credito?.score
                            ? formatToReal(data.limite_credito.score)
                            : "Indisponível"}
                        </div>

                        <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-3 text-[11px] text-slate-600">
                          Limite de crédito estimado a partir de informações
                          comportamentais e cadastrais da empresa.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Score de Risco */}
                <div>
                  <TitleStripe>
                    <div className="flex items-center gap-2">
                      <TfiBarChart className="text-blue-700" />{" "}
                      <span>Score de risco</span>
                    </div>
                  </TitleStripe>
                  <div className="flex  flex-row items-center gap-6">
                    {+data?.score?.score_pj?.score !== 2 ? (
                      <div className="flex-1 mt-4">
                        <div className="grid grid-cols-2 gap-4">
                          {/* Card Score com barra de cor forte */}
                          <div className="p-6 bg-white rounded-2xl border border-gray-200 flex flex-col gap-3">
                            <div className="flex items-center gap-2">
                              <HiShieldCheck
                                className="text-green-500"
                                size={20}
                              />
                              <span className="text-sm font-medium text-gray-600">
                                Score de Risco
                              </span>
                            </div>

                            <div className="flex items-center justify-between">
                              <div className="text-4xl font-extrabold text-gray-800">
                                {+data?.score?.score_pj?.score}
                              </div>
                            </div>

                            <div className="text-xs text-gray-500 -mt-2">
                              Escala de 0 a 1000
                            </div>

                            <div className="relative w-full h-6 bg-gray-200 rounded-full overflow-hidden">
                              <div className="absolute inset-0 bg-gradient-to-r from-red-500 via-yellow-400 to-green-500 rounded-full"></div>
                              <div
                                className="absolute top-1/2 -translate-y-1/2 w-6 h-6 rounded-full border-2 border-white bg-white z-10 transition-all duration-700 ease-out"
                                style={{
                                  left: `calc(${
                                    (+data?.score?.score_pj?.score / 1000) * 100
                                  }% - 12px)`,
                                }}
                              ></div>
                              <div className="absolute inset-0 flex items-center justify-center">
                                <span className="text-sm font-semibold text-white drop-shadow">
                                  {+data?.score?.score_pj?.score}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Card Probabilidade com barra de cor forte */}
                          <div className="p-6 bg-white rounded-2xl border border-gray-200 flex flex-col gap-3">
                            <div className="flex items-center gap-2">
                              <HiExclamationCircle
                                className="text-red-500"
                                size={20}
                              />
                              <span className="text-sm font-medium text-gray-600">
                                Probabilidade de Inadimplência
                              </span>
                            </div>

                            <div className="text-4xl font-extrabold text-gray-800">
                              {prob}%
                            </div>

                            <div className="text-xs text-gray-500 -mt-2">
                              Quanto menor, melhor
                            </div>

                            <div className="relative w-full h-6 bg-gray-200 rounded-full overflow-hidden">
                              <div className="absolute inset-0 bg-gradient-to-r from-green-500 via-yellow-400 to-red-500 rounded-full"></div>
                              <div
                                className="absolute top-1/2 -translate-y-1/2 w-6 h-6 rounded-full border-2 border-white shadow bg-white z-10 transition-all duration-700 ease-out"
                                style={{
                                  left: `calc(${parseInt(prob)}% - 12px)`,
                                }}
                              ></div>
                              <div className="absolute inset-0 flex items-center justify-center">
                                <span className="text-sm font-semibold text-white drop-shadow">
                                  {parseInt(prob)}%
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800 text-sm mt-4 w-full">
                        <p className="font-medium">
                          Não foi possível calcular o score desta empresa.
                        </p>
                        <p className="mt-1">
                          É necessário verificar as outras informações
                          disponíveis no relatório para uma análise completa.
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Alerta Legal */}
                <div className="text-xs text-gray-600 border-t pt-4">
                  <span className="font-semibold text-gray-700">
                    Importante:
                  </span>{" "}
                  a decisão de aprovação ou não do crédito é de exclusiva
                  responsabilidade do concedente. As informações prestadas pelo
                  provedor têm como objetivo subsidiar essas decisões e, em
                  hipótese alguma, devem ser utilizadas como justificativa única
                  para concessão ou recusa de crédito.
                </div>
              </div>

              {statusConsumidor && (
                <div className="mt-8 w-full">
                  <div
                    className={`flex items-center justify-between gap-4 rounded-2xl border px-4 py-4 text-sm shadow-sm ${
                      statusConsumidorContainsNao
                        ? "border-rose-200 bg-rose-50/70 text-rose-700"
                        : "border-emerald-200 bg-emerald-50/70 text-emerald-700"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`flex h-10 w-10 items-center justify-center rounded-full border ${
                          statusConsumidorContainsNao
                            ? "border-rose-200 bg-rose-100 text-rose-700"
                            : "border-emerald-200 bg-emerald-100 text-emerald-700"
                        }`}
                      >
                        {statusConsumidorContainsNao ? (
                          <HiThumbDown className="h-5 w-5" />
                        ) : (
                          <HiThumbUp className="h-5 w-5" />
                        )}
                      </div>
                      <div>
                        <div className="text-[10px] font-semibold uppercase tracking-[0.25em] opacity-70">
                          Status do consumidor
                        </div>
                        <div className="text-base font-semibold text-slate-900">
                          {statusConsumidor}
                        </div>
                      </div>
                    </div>
                    <div className="hidden rounded-full border border-white/40 bg-white/30 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] sm:block">
                      {statusConsumidorContainsNao ? "Atenção" : "Regular"}
                    </div>
                  </div>
                </div>
              )}

              {!statusConsumidorContainsNao && (
                <>
                  {" "}
                  <div>
                    <TitleStripe className="mb-4">
                      Pagamentos Pontuais
                    </TitleStripe>
                    {data?.pagamento_pontual ? (
                      <>
                        <div className="mt-3 grid gap-3">
                          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white ">
                            <div className="border-b border-slate-200 bg-slate-50 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-500">
                              Pagamentos pontuais por período (Últimos 12 meses)
                            </div>
                            <div className="overflow-x-auto">
                              <table className="min-w-full">
                                <thead>
                                  <tr>
                                    {data?.pagamento_pontual?.lista_indicador_comportamento?.item_indicador?.map(
                                      (credito, index) => (
                                        <th
                                          key={index}
                                          className="px-2 py-2 text-center text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500"
                                        >
                                          {formatDateToYY(credito.mes_ano)}
                                        </th>
                                      )
                                    )}
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr className="border-t border-slate-100">
                                    {data?.pagamento_pontual?.lista_indicador_comportamento?.item_indicador?.map(
                                      (credito, index) => {
                                        const valor = parseFloat(
                                          credito?.pontuacao_mensal
                                        );
                                        return (
                                          <td
                                            key={index}
                                            className="px-2 py-3 text-center text-sm font-semibold text-slate-700 tabular-nums"
                                          >
                                            {!isNaN(valor) ? `${valor}%` : "–"}
                                          </td>
                                        );
                                      }
                                    )}
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </div>

                          <div className="flex flex-wrap items-center justify-center gap-3 text-xs text-slate-500">
                            <span className="inline-flex items-center gap-2">
                              <span className="h-2 w-2 rounded-full bg-emerald-500" />
                              100%: Pagamentos pontuais
                            </span>
                            <span className="text-slate-300">|</span>
                            <span className="inline-flex items-center gap-2">
                              <span className="h-2 w-2 rounded-full bg-rose-500" />
                              0%: Pagamentos não pontuais
                            </span>
                          </div>
                        </div>
                      </>
                    ) : (
                      <NoOcurrence />
                    )}
                  </div>
                  <div>
                    <TitleStripe className="mb-4">
                      Pagamentos em atrasos
                    </TitleStripe>

                    {data?.pagamento_atrasado ? (
                      <>
                        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white ">
                          <div className="border-b border-slate-200 bg-slate-50 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-500">
                            Pagamentos em atrasos por período (Últimos 12 meses)
                          </div>
                          <div className="overflow-x-auto">
                            <table className="min-w-full">
                              <thead>
                                <tr>
                                  <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                                    Tipo
                                  </th>
                                  {Object.values(
                                    data.pagamento_atrasado
                                  )[0]?.lista_indicador_comportamento?.item_indicador?.map(
                                    (item, idx) => (
                                      <th
                                        key={idx}
                                        className="px-2 py-2 text-center text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500"
                                      >
                                        {item.mes_ano}
                                      </th>
                                    )
                                  )}
                                </tr>
                              </thead>

                              <tbody className="divide-y divide-slate-100">
                                {Object.entries(data.pagamento_atrasado).map(
                                  ([sectionKey, sectionValue], i) => {
                                    const indicadores =
                                      sectionValue
                                        ?.lista_indicador_comportamento
                                        ?.item_indicador;

                                    if (!Array.isArray(indicadores))
                                      return null;

                                    return (
                                      <tr key={i}>
                                        <td className="px-3 py-3 text-left text-xs font-medium capitalize text-slate-700">
                                          {sectionKey.replaceAll("_", " ")}
                                        </td>
                                        {indicadores.map((item, idx) => {
                                          const valor = parseFloat(
                                            item.pontuacao_mensal
                                          );
                                          return (
                                            <td
                                              key={idx}
                                              className="px-2 py-3 text-center text-sm font-semibold text-slate-700 tabular-nums"
                                            >
                                              {!isNaN(valor)
                                                ? `${valor}%`
                                                : "–"}
                                            </td>
                                          );
                                        })}
                                      </tr>
                                    );
                                  }
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                        <div className="mt-3 flex flex-wrap items-center justify-center gap-3 text-xs text-slate-500">
                          <span className="inline-flex items-center gap-2">
                            <span className="h-2 w-2 rounded-full bg-emerald-500" />
                            0%: Pagamentos em dia
                          </span>
                          <span className="text-slate-300">|</span>
                          <span className="inline-flex items-center gap-2">
                            <span className="h-2 w-2 rounded-full bg-rose-500" />
                            100%: Pagamentos em atrasos
                          </span>
                        </div>
                      </>
                    ) : (
                      <NoOcurrence />
                    )}
                  </div>
                  <div>
                    <TitleStripe className="mb-4">
                      Compromissos e valor dos títulos
                    </TitleStripe>
                    {periodosIndicadores.length > 0 ? (
                      <>
                        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white ">
                          <div className="border-b border-slate-200 bg-slate-50 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-500">
                            Total de obrigações financeiras por período (últimos
                            12 meses)
                          </div>
                          <div className="overflow-x-auto">
                            <table className="min-w-full">
                              <thead>
                                <tr>
                                  <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                                    Indicador
                                  </th>
                                  {periodosIndicadores.map((periodo, index) => (
                                    <th
                                      key={`${periodo}-${index}`}
                                      className="px-2 py-2 text-center text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500"
                                    >
                                      {formatDateToYY(periodo)}
                                    </th>
                                  ))}
                                </tr>
                              </thead>

                              <tbody className="divide-y divide-slate-100">
                                <tr>
                                  <td className="px-3 py-3 text-left text-xs font-medium text-slate-700">
                                    Qtd. de títulos
                                  </td>
                                  {periodosIndicadores.map((periodo, index) => (
                                    <td
                                      key={`compromisso-${periodo}-${index}`}
                                      className="px-2 py-3 text-center text-xs font-semibold text-slate-700 tabular-nums"
                                    >
                                      {formatQTD(
                                        getCompromissoIndicadorPorPeriodo(
                                          periodo
                                        )?.pontuacao_mensal
                                      )}
                                    </td>
                                  ))}
                                </tr>
                                <tr>
                                  <td className="px-3 py-3 text-left text-xs font-medium text-slate-700">
                                    Valor dos títulos
                                  </td>
                                  {periodosIndicadores.map((periodo, index) => (
                                    <td
                                      key={`valor-titulo-${periodo}-${index}`}
                                      className="px-2 py-3 text-center text-2xs font-semibold text-slate-700 tabular-nums"
                                    >
                                      {formatValorTitulos(
                                        getCreditoIndicadorPorPeriodo(periodo)
                                          ?.pontuacao_mensal
                                      )}
                                    </td>
                                  ))}
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                        <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
                          Descrição: Número total de obrigações financeiras
                          registradas no nome do consumidor ou empresa, como
                          boletos, duplicatas, carnês ou contratos de crédito.
                          Indica a quantidade de compromissos ativos assumidos,
                          independentemente do valor.
                        </div>
                      </>
                    ) : (
                      <NoOcurrence />
                    )}
                  </div>
                  <div>
                    <TitleStripe className="mb-4">
                      Comprometimento Futuro
                    </TitleStripe>
                    {data?.comprometimento_futuro && periodos.length > 0 ? (
                      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white ">
                        <div className="border-b border-slate-200 bg-slate-50 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-500">
                          Indicadores por período
                        </div>
                        <div className="overflow-x-auto">
                          <table className="min-w-full">
                            <thead>
                              <tr>
                                <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                                  Indicador
                                </th>
                                {periodos.map((periodo, index) => (
                                  <th
                                    key={`${periodo}-${index}`}
                                    className="px-2 py-2 text-center text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500"
                                  >
                                    {formatDateToYY(periodo)}
                                  </th>
                                ))}
                              </tr>
                            </thead>

                            <tbody className="divide-y divide-slate-100">
                              <tr>
                                <td className="px-3 py-3 text-left text-sm font-medium text-slate-700">
                                  Quantidade de títulos
                                </td>
                                {periodos.map((periodo, index) => {
                                  const valor =
                                    getCompromissoPorPeriodo(
                                      periodo
                                    )?.pontuacao;
                                  return (
                                    <td
                                      key={`qtd-${periodo}-${index}`}
                                      className="px-2 py-3 text-center text-sm font-semibold text-slate-700 tabular-nums"
                                    >
                                      {formatQTD(valor)}
                                    </td>
                                  );
                                })}
                              </tr>
                              <tr>
                                <td className="px-3 py-3 text-left text-sm font-medium text-slate-700">
                                  Valor dos títulos
                                </td>
                                {periodos.map((periodo, index) => {
                                  const valor =
                                    getCreditoPorPeriodo(periodo)?.pontuacao;
                                  return (
                                    <td
                                      key={`valor-${periodo}-${index}`}
                                      className="px-2 py-3 text-center text-sm font-semibold text-slate-700 tabular-nums"
                                    >
                                      {formatValorTitulos(valor)}
                                    </td>
                                  );
                                })}
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ) : (
                      <NoOcurrence />
                    )}
                  </div>
                  {hasDemaisInformacoes && (
                    <div className="mb-6 grid gap-4 lg:grid-cols-2 mt-6">
                      {eventosImportantes.length > 0 && (
                        <div className="rounded-3xl bg-gray-50 p-6 border border-gray-200">
                          <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-[0.2em] text-gray-800">
                            <span>Acontecimentos importantes</span>
                            <span className="text-[0.55rem]">Registros</span>
                          </div>
                          <div className="mt-6 space-y-4 max-h-72 overflow-y-auto pr-1">
                            {eventosImportantes.map((evento, index) => (
                              <div
                                key={`${evento.indicador ?? "evento"}-${index}`}
                                className="flex items-start justify-between gap-4 rounded-2xl bg-white px-3 py-3 shadow-inner"
                              >
                                <div className="min-w-0 space-y-1">
                                  <p className="text-sm font-semibold leading-tight">
                                    {evento.indicador || "—"}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    {evento.periodo || "—"}
                                  </p>
                                </div>
                                <div className="flex-shrink-0 rounded-full border border-white/60 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-widest">
                                  {evento.pontuacao
                                    ? formatToReal(evento.pontuacao * 1000)
                                    : "—"}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {tempoRelacionamento.length > 0 && (
                        <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
                          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                            Tempo de relacionamento com fornecedores
                          </div>
                          <div className="mt-5 space-y-4">
                            {tempoRelacionamento.map((item, index) => {
                              const valorNumerico = Number(item?.valor ?? 0);
                              const barWidth = getTempoBarWidth(valorNumerico);
                              return (
                                <div
                                  key={`${item?.periodo ?? "tempo"}-${index}`}
                                  className="space-y-2"
                                >
                                  <div className="flex items-center justify-between text-sm font-semibold text-slate-700">
                                    <span>{item?.periodo || "—"}</span>
                                    <span className="text-[0.65rem] font-medium uppercase tracking-widest text-slate-400">
                                      {item?.valor ?? "0"}
                                    </span>
                                  </div>
                                  <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
                                    <div
                                      className="h-full rounded-full bg-gradient-to-r from-emerald-400 via-sky-500 to-indigo-500 transition-all duration-200"
                                      style={{ width: `${barWidth}%` }}
                                    />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}

              <div>
                <TitleStripe className="mb-5">Sócios</TitleStripe>
                {data?.socios ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm text-left text-gray-700 border border-gray-200">
                      <thead className="bg-gray-100 text-xs uppercase font-semibold text-gray-600">
                        <tr>
                          <th className="px-3 py-2">Nome</th>
                          <th className="px-3 py-2">Part.</th>
                          <th className="px-3 py-2">Assina</th>
                          <th className="px-3 py-2">Fraude</th>
                          <th className="px-3 py-2">Situação</th>
                          <th className="px-3 py-2">Restrições</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(Array.isArray(data?.socios?.socio)
                          ? data?.socios?.socio
                          : [data?.socios?.socio]
                        ).map((socio, index) => {
                          const restricoes = socio?.restricoes_documento || {};
                          return (
                            <tr
                              key={index}
                              className={
                                index % 2 === 0 ? "bg-white" : "bg-gray-50"
                              }
                            >
                              <td className="px-3 py-2">
                                <div>{socio?.nome_empresa}</div>
                                <div className="text-xs">
                                  Doc.: {socio?.cpf_cnpj}
                                </div>
                              </td>
                              <td className="px-3 py-2">
                                {socio?.participacao}%
                              </td>
                              <td className="px-3 py-2">
                                {socio?.assina_empresa === "S" ? "Sim" : "Não"}
                              </td>
                              <td className="px-3 py-2">
                                {socio?.indicador_fraude}
                              </td>
                              <td className="px-3 py-2">
                                {socio?.situacao_documento}
                              </td>
                              <td className="px-3 py-2 text-xs leading-5">
                                <div>
                                  🛑 Pend.:{" "}
                                  {restricoes?.pendencias_restricoes
                                    ?.quantidade || 0}
                                </div>
                                <div>
                                  📄 Prot.:{" "}
                                  {restricoes?.protestos?.quantidade || 0}
                                </div>
                                <div>
                                  💸 Cheques:{" "}
                                  {restricoes?.cheques_sem_fundo?.quantidade ||
                                    0}
                                </div>
                                <div>
                                  ⚖️ Ações:{" "}
                                  {restricoes?.acoes_judiciais?.quantidade || 0}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <NoOcurrence />
                )}
              </div>

              <div>
                <TitleStripe className="mb-5">
                  <div className="flex items-center gap-2">
                    <LuBuilding2 className="text-blue-700" />
                    <span>
                      Participações dos Sócios e Adm. em Outras Empresas
                    </span>
                  </div>
                </TitleStripe>

                {!data?.participacoes_socios_adm_outras_empresas?.mensagem ? (
                  sociosAdmOutrasEmpresas.length ? (
                    sociosAdmOutrasEmpresas.map((socioP, index) => (
                      <div key={index} className="mt-5">
                        <div className="text-sm font-semibold text-blue-700 underline">
                          {socioP?.nome_socio || "—"}
                        </div>
                        {renderParticipacoesTable(socioP?.socios_participantes)}
                      </div>
                    ))
                  ) : (
                    <NoOcurrence />
                  )
                ) : (
                  <NoOcurrence />
                )}
              </div>

              <div>
                <TitleStripe className="mb-5">Cheques Sem Fundos</TitleStripe>
                {!!data?.cheques_sem_fundo?.total_cheques_sem_fundos ? (
                  <>
                    <ChequesSemFundos
                      quantidadeTotal={
                        data?.cheques_sem_fundo?.total_cheques_sem_fundos
                      }
                      totalNomes={
                        data?.cheques_sem_fundo?.total_nomes_relacionados
                      }
                      chequesSemFundos={
                        Array.isArray(
                          data?.cheques_sem_fundo?.ultimas_ocorrencias
                            ?.cheque_sem_fundo
                        )
                          ? data?.cheques_sem_fundo?.ultimas_ocorrencias
                              ?.cheque_sem_fundo
                          : [
                              ...[],
                              data?.cheques_sem_fundo?.ultimas_ocorrencias
                                ?.cheque_sem_fundo,
                            ]
                      }
                    />
                  </>
                ) : (
                  <NoOcurrence />
                )}
              </div>
              <div>
                <TitleStripe className="mb-5">Cheques Sustados</TitleStripe>
                {data?.cheques_sustados?.ultimas_ocorrencias?.cheques_sustado
                  ?.length > 0 ? (
                  <>
                    <ChequesSustados
                      periodoInicial={data?.cheques_sustados?.periodo_inicial}
                      periodoFinal={data?.cheques_sustados?.periodo_final}
                      quantidadeTotal={data?.cheques_sustados?.total}
                      chequesSustados={
                        Array.isArray(
                          data?.cheques_sustados?.ultimas_ocorrencias
                            ?.cheques_sustado
                        )
                          ? data?.cheques_sustados?.ultimas_ocorrencias
                              ?.cheques_sustado
                          : [
                              ...[],
                              data?.cheques_sustados?.ultimas_ocorrencias
                                ?.cheques_sustado,
                            ]
                      }
                    />
                  </>
                ) : (
                  <NoOcurrence />
                )}
              </div>

              <div>
                <TitleStripe className="mb-5">Protestos</TitleStripe>
                {!!data?.protestos?.total_protestos ? (
                  <Protestos
                    quantidadeTotal={data?.protestos?.total_protestos}
                    valorTotal={data?.protestos?.valor_total}
                    primeiroValor={data?.protestos?.valor_primeiro}
                    protestos={
                      Array.isArray(
                        data?.protestos?.ultimas_ocorrencias?.protesto
                      )
                        ? data?.protestos?.ultimas_ocorrencias?.protesto
                        : [
                            ...[],
                            data?.protestos?.ultimas_ocorrencias?.protesto,
                          ]
                    }
                  />
                ) : (
                  <NoOcurrence />
                )}
              </div>

              <div>
                <TitleStripe className="mb-5">Negativações</TitleStripe>
                {data?.pendencias_restricoes?.ultimas_ocorrencias
                  ?.pendencia_restricao ? (
                  <>
                    <PendenciasFinanceiras
                      quantidadeTotal={
                        data?.pendencias_restricoes?.total_pendencias
                      }
                      dataMaiorPendencia={
                        data?.pendencias_restricoes?.data_maior
                      }
                      valorTotal={data?.pendencias_restricoes?.valor}
                      pendenciasFinanceiras={
                        Array.isArray(
                          data?.pendencias_restricoes?.ultimas_ocorrencias
                            ?.pendencia_restricao
                        )
                          ? data?.pendencias_restricoes?.ultimas_ocorrencias
                              ?.pendencia_restricao
                          : [
                              ...[],
                              data?.pendencias_restricoes?.ultimas_ocorrencias
                                ?.pendencia_restricao,
                            ]
                      }
                    />
                  </>
                ) : (
                  <NoOcurrence />
                )}
              </div>

              <div>
                <TitleStripe className="mb-5">Pendências Serasa</TitleStripe>

                {data?.crednet?.consultaCredito?.pendenciasFinanceiras?.length >
                0 ? (
                  <>
                    <PendenciasFinanceiras
                      quantidadeTotal={
                        data?.crednet?.consultaCredito?.resumoConsulta
                          ?.pendenciasFinanceiras?.quantidadeTotal
                      }
                      valorTotal={
                        data?.crednet?.consultaCredito?.resumoConsulta
                          ?.pendenciasFinanceiras?.valorTotal
                      }
                      pendenciasFinanceiras={
                        data?.crednet?.consultaCredito?.pendenciasFinanceiras
                      }
                    />
                  </>
                ) : (
                  <NoOcurrence />
                )}
              </div>

              <div>
                <TitleStripe className="mb-5">Pendências Quod</TitleStripe>

                {data?.quod?.pendenciasFinanceiras?.length > 0 ? (
                  <>
                    <PendenciasFinanceiras
                      quantidadeTotal={
                        data?.quod?.resumoConsulta?.pendenciasFinanceiras
                          ?.quantidadeTotal
                      }
                      valorTotal={
                        data?.quod?.resumoConsulta?.pendenciasFinanceiras
                          ?.valorTotal
                      }
                      pendenciasFinanceiras={data?.quod?.pendenciasFinanceiras}
                    />
                  </>
                ) : (
                  <NoOcurrence />
                )}
              </div>

              <div>
                <TitleStripe className="mb-5">Ações Cíveis</TitleStripe>
                {!!data?.defineLimite?.acoes_civeis?.total_acoes ? (
                  <>
                    <AcoesCiveis
                      quantidadeTotal={
                        data?.defineLimite?.acoes_civeis?.total_acoes
                      }
                      primeiroValor={
                        data?.defineLimite?.acoes_civeis?.valor_primeiro
                      }
                      valorTotal={data?.defineLimite?.acoes_civeis?.valor_total}
                      acoesCiveis={
                        Array.isArray(
                          data?.defineLimite?.acoes_civeis?.ultimas_ocorrencias
                            ?.acao_civel
                        )
                          ? data?.defineLimite?.acoes_civeis
                              ?.ultimas_ocorrencias?.acao_civel
                          : [
                              ...[],
                              data?.defineLimite?.acoes_civeis
                                ?.ultimas_ocorrencias?.acao_civel,
                            ]
                      }
                    />
                  </>
                ) : (
                  <NoOcurrence />
                )}
              </div>

              <div>
                <TitleStripe className="mb-5">
                  Falências/Recuperações Judiciais
                </TitleStripe>
                {data &&
                !data?.defineLimite?.falencias_recuperacao_judicial
                  ?.mensagem ? (
                  <FalenciaRecuperacaoJudicial
                    falenciaRecuperacaoJudicial={
                      Array.isArray(
                        data?.defineLimite?.falencias_recuperacao_judicial
                          ?.ultimas_ocorrencias?.falencia_recuperacao_judicial
                      )
                        ? data?.defineLimite?.falencias_recuperacao_judicial
                            ?.ultimas_ocorrencias?.falencia_recuperacao_judicial
                        : [
                            ...[],
                            data?.defineLimite?.falencias_recuperacao_judicial
                              ?.ultimas_ocorrencias
                              ?.falencia_recuperacao_judicial,
                          ]
                    }
                  />
                ) : (
                  <NoOcurrence />
                )}
              </div>

              <div>
                <TitleStripe className="mb-5">
                  Histórico de Consultas
                </TitleStripe>

                {data?.defineLimite?.consultas?.ultimas_consultas?.consulta
                  ?.length > 0 ? (
                  <>
                    <HistoricoConsultas
                      quantidadeTotal={
                        data?.defineLimite?.consultas?.quantidade_total
                      }
                      periodoInicial={
                        data?.defineLimite?.consultas?.periodo_inicial
                      }
                      periodoFinal={
                        data?.defineLimite?.consultas?.periodo_final
                      }
                      historicoConsultas={
                        Array.isArray(
                          data?.defineLimite?.consultas?.ultimas_consultas
                            ?.consulta
                        )
                          ? data?.defineLimite?.consultas?.ultimas_consultas?.consulta?.map(
                              (hist) => ({
                                data: hist.data,
                                empresa: hist.razao_social,
                              })
                            )
                          : [
                              ...[],
                              {
                                data: data?.defineLimite?.consultas
                                  ?.ultimas_consultas?.consulta?.data,
                                empresa:
                                  data?.defineLimite?.consultas
                                    ?.ultimas_consultas?.consulta?.razao_social,
                              },
                            ]
                      }
                    />
                  </>
                ) : (
                  <NoOcurrence />
                )}

                <div className="border border-gray-300 rounded-lg mt-3">
                  <Table striped>
                    <Table.Head>
                      {data?.defineLimite?.consultas?.consultas_mes?.map(
                        (consulta, index) => (
                          <Table.HeadCell
                            key={index}
                            className="text-center text-black px-1"
                            style={{ background: "#EBF5FF" }}
                          >
                            {formatDateToDDMM(consulta.mes_ano)}
                          </Table.HeadCell>
                        )
                      )}
                    </Table.Head>

                    <Table.Body className="divide-y">
                      <Table.Row className="bg-white dark:border-gray-700 dark:bg-gray-800">
                        {data?.defineLimite?.consultas?.consultas_mes?.map(
                          (consult, index) => (
                            <Table.Cell
                              className="font-medium text-gray-900 dark:text-white text-center text-black py-1 px-1"
                              key={index}
                            >
                              {consult.quantidade}
                            </Table.Cell>
                          )
                        )}
                      </Table.Row>
                    </Table.Body>
                  </Table>
                </div>
              </div>

              <FooterQuery hasHeader={hasHeaderPDF} />
            </Card>
          ) : (
            <QueryLoading />
          )}
        </div>
      )}
    </>
  );
};

export default CompletaPlusCNPJQuery;
